import { useState } from "react";
import { type SxProps, type Theme } from "@mui/material/styles";

import Icons from "assets";
import { Chip } from "ui/chip";
import { Button } from "ui/button";
import { Switch } from "ui/switch";
import toast from "components/partials/toast/toast";
import CollegeProfileEmailTypeSendersForm from "containers/admin/clients/client/college-profile-page/college-profile-email-type-senders-form";

import {
  useSetDefaultEmailType,
  useUpdateEmailTypeSenderDetails,
  type EmailType,
} from "hooks/api/use-email-types";

import { getErrorMessage } from "utilities";

import type { IterableEmailSenderDetailRequest } from "generated/schemas";

export type CollegeProfileEmailTypeCardProps = {
  clientId: string;
  emailType: EmailType;
  canEdit: boolean;
};

const EDIT_SENDERS_BUTTON_SX: SxProps<Theme> = { alignSelf: "flex-start" };

const CollegeProfileEmailTypeCard = ({
  clientId,
  emailType,
  canEdit,
}: CollegeProfileEmailTypeCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditingSenders, setIsEditingSenders] = useState(false);

  const setDefaultEmailType = useSetDefaultEmailType(clientId);
  const updateSenderDetails = useUpdateEmailTypeSenderDetails(clientId);

  const handleDefaultToggle = (isChecked: boolean) => {
    if (!isChecked) {
      return;
    }

    setDefaultEmailType.mutate(emailType.id, {
      onError: (err) => {
        toast.error({
          data: { title: "Could not set default email type", message: getErrorMessage(err) },
        });
      },
    });
  };

  const handleSaveSenders = (senders: IterableEmailSenderDetailRequest[]) => {
    updateSenderDetails.mutate(
      { emailTypeId: emailType.id, senders },
      {
        onSuccess: () => {
          toast.success({ data: { title: "Sender details saved" } });
          setIsEditingSenders(false);
        },
        onError: (err) => {
          toast.error({
            data: { title: "Could not save sender details", message: getErrorMessage(err) },
          });
        },
      },
    );
  };

  const senders = emailType.senders ?? [];

  return (
    <section className="flex flex-col rounded-md border border-gray-200">
      <div className="flex items-center justify-between gap-3 p-4">
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
          onClick={() => setIsExpanded(!isExpanded)}
          aria-expanded={isExpanded}>
          {isExpanded ? (
            <Icons.ChevronDown className="size-4 shrink-0 text-gray-900" />
          ) : (
            <Icons.ChevronRight className="size-4 shrink-0 text-gray-900" />
          )}
          <span className="flex flex-col">
            <span className="font-semibold text-navy-800">{emailType.externalName}</span>
            <span className="text-xs text-gray-500">ID {emailType.externalId}</span>
          </span>
        </button>
        <Switch
          toggleName="Default"
          checked={emailType.defaultForClient}
          onChange={canEdit ? handleDefaultToggle : undefined}
          disabled={!canEdit || setDefaultEmailType.isPending}
        />
      </div>

      {isExpanded && (
        <div className="flex flex-col gap-3 border-t border-gray-200 p-4">
          {isEditingSenders ? (
            <CollegeProfileEmailTypeSendersForm
              emailType={emailType}
              isSaving={updateSenderDetails.isPending}
              onCancel={() => setIsEditingSenders(false)}
              onSave={handleSaveSenders}
            />
          ) : (
            <>
              {senders.length ? (
                <div className="flex flex-col gap-6">
                  {senders.map((sender, index) => (
                    <div key={sender.id ?? index} className="flex flex-col gap-3">
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-bold text-navy-800">Sender {index + 1}</span>
                        {sender.defaultForEmailType && (
                          <Chip color="primary" label="Default" size="small" />
                        )}
                      </div>
                      <dl className="flex flex-col gap-3">
                        <div className="flex flex-col gap-0">
                          <dt className="text-sm font-normal text-gray-500">Sender name</dt>
                          <dd className="text-base font-medium text-gray-900">
                            {sender.senderName}
                          </dd>
                        </div>
                        <div className="flex flex-col gap-0">
                          <dt className="text-sm font-normal text-gray-500">Sender email</dt>
                          <dd className="text-base font-medium text-gray-900">
                            {sender.senderEmail}
                          </dd>
                        </div>
                        <div className="flex flex-col gap-0">
                          <dt className="text-sm font-normal text-gray-500">Reply-to email</dt>
                          <dd className="text-base font-medium text-gray-900">
                            {sender.replyToEmail}
                          </dd>
                        </div>
                      </dl>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-base text-gray-500">There are no sender details created yet.</p>
              )}
              {canEdit && (
                <Button
                  size="medium"
                  variant="contained"
                  sx={EDIT_SENDERS_BUTTON_SX}
                  startIcon={<Icons.Edit />}
                  onClick={() => setIsEditingSenders(true)}>
                  Edit senders
                </Button>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
};

export default CollegeProfileEmailTypeCard;
